/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import { Field } from 'app/components/organisms/field/field';
import { LogBodyRendererFlat } from './log-body-renderer-flat';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useQuery } from '@tanstack/react-query';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import React from 'react';
import ReactJson from 'react-json-view';
import qs from 'qs';

/**
 * `Props` type.
 */

type Props = {
  item: any;
};

/**
 * Parse body to extract request details.
 */

function parseBody(body: any): any {
  try {
    return typeof body === 'string' ? JSON.parse(body) : body;
  } catch (error) {
    return null;
  }
}

/**
 * Render headers in a clean format.
 */

function renderHeaders(headers: Record<string, string>): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {Object.entries(headers).map(([key, value]) => (
        <div
          key={key}
          style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '8px', fontSize: '12px', lineHeight: '1.6' }}
        >
          <span style={{ color: '#666', fontWeight: 600 }}>
            {key}
            {':'}
          </span>
          <span style={{ color: '#000', wordBreak: 'break-all' }}>{value}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Get status code color based on HTTP status ranges.
 */

function getStatusColor(statusCode: number): string {
  if (statusCode >= 200 && statusCode < 300) return '#52c41a'; // Success - green
  if (statusCode >= 300 && statusCode < 400) return '#1890ff'; // Redirect - blue
  if (statusCode >= 400 && statusCode < 500) return '#faad14'; // Client error - orange
  if (statusCode >= 500) return '#f5222d'; // Server error - red
  return '#d9d9d9'; // Unknown - gray
}

/**
 * Method colors for HTTP methods.
 */

const methodColors: Record<string, string> = {
  GET: '#52c41a',
  POST: '#1890ff',
  PUT: '#faad14',
  PATCH: '#13c2c2',
  DELETE: '#f5222d'
};

/**
 * Split URL into base and query string.
 */

function splitUrl(url: string): { baseUrl: string; queryString?: string } {
  const urlParts = url.split('?');
  return {
    baseUrl: urlParts[0],
    queryString: urlParts[1]
  };
}

/**
 * Export `LogDetail` component.
 */

export function LogDetail({ item }: Props): JSX.Element {
  const parsedBody = parseBody(item.body);
  const messages = parsedBody?.messages;
  const firstMessage = messages && messages.length > 0 ? messages[0] : null;
  const context = parsedBody?.context;
  const isRequest = item.type === 'request';
  const isResponse = item.type === 'response';
  const isError = item.type === 'error';
  const isInfo = item.type === 'info';
  const isVerbose = item.type === 'verbose';
  const isTask = item.type === 'task';
  const client = useRequestProvider();

  // Fetch user data if userId exists
  const { data: userData, isLoading: isUserLoading } = useQuery({
    queryKey: ['user', item.userId],
    queryFn: async () => {
      if (!item.userId) return null;
      const url = resolveAppUrl(apiEndpoints.usersById, { interpolations: { userId: item.userId } });
      const response = await client.get(url);
      return response.data?.data ?? response.data ?? null;
    },
    enabled: !!item.userId
  });

  // Prepare raw data with parsed body
  const rawData = {
    ...item,
    body: parsedBody || item.body
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Field title={'ID'} value={String(item.id)} />

      <Field title={'Correlation ID'} value={String(item.correlationId)} />

      <Field title={'Created At'} value={<DateTime date={item.createdAt} />} />

      <Field title={'User ID'} value={item.userId ? String(item.userId) : 'N/A'} />

      {isInfo && item.userId && userData && (
        <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
          <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>{'User'}</div>
          <div style={{ paddingLeft: '16px', fontSize: '11px' }}>
            <ReactJson
              collapsed={1}
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={true}
              name={false}
              src={userData}
              style={{ fontSize: '11px' }}
              theme={{
                base00: '#ffffff',
                base01: '#f5f5f5',
                base02: '#e0e0e0',
                base03: '#999999',
                base04: '#666666',
                base05: '#333333',
                base06: '#1a1a1a',
                base07: '#000000',
                base08: '#d32f2f',
                base09: '#f57c00',
                base0A: '#fbc02d',
                base0B: '#388e3c',
                base0C: '#0097a7',
                base0D: '#1976d2',
                base0E: '#7b1fa2',
                base0F: '#5d4037'
              }}
            />
          </div>
        </div>
      )}

      {isInfo && item.userId && isUserLoading && (
        <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
          <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>{'User'}</div>
          <div style={{ paddingLeft: '16px', fontSize: '13px', color: '#999' }}>{'Loading user data...'}</div>
        </div>
      )}

      <Field title={'Level'} value={item.level} />

      <Field title={'Type'} value={item.type} />

      <Field title={'App Version'} value={item.version} />

      {/* Request-specific fields */}
      {isRequest && context && (
        <>
          {context.method && <Field title={'Method'} value={context.method} />}

          {context.url &&
            (() => {
              const { baseUrl, queryString } = splitUrl(context.url);
              return (
                <>
                  <Field
                    title={'URL'}
                    value={
                      <span style={{ fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>
                        {baseUrl}
                      </span>
                    }
                  />
                  {queryString && (
                    <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                      <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>
                        {'Query String'}
                      </div>
                      <div style={{ paddingLeft: '16px', fontSize: '11px' }}>
                        <ReactJson
                          collapsed={1}
                          displayDataTypes={false}
                          displayObjectSize={false}
                          enableClipboard={true}
                          name={false}
                          src={qs.parse(queryString)}
                          style={{ fontSize: '11px' }}
                          theme={{
                            base00: '#ffffff',
                            base01: '#f5f5f5',
                            base02: '#e0e0e0',
                            base03: '#999999',
                            base04: '#666666',
                            base05: '#333333',
                            base06: '#1a1a1a',
                            base07: '#000000',
                            base08: '#d32f2f',
                            base09: '#f57c00',
                            base0A: '#fbc02d',
                            base0B: '#388e3c',
                            base0C: '#0097a7',
                            base0D: '#1976d2',
                            base0E: '#7b1fa2',
                            base0F: '#5d4037'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

          {firstMessage?.ipAddress && (
            <Field
              title={'IP Address'}
              value={<span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{firstMessage.ipAddress}</span>}
            />
          )}

          {firstMessage?.userAgent && (
            <Field
              title={'User Agent'}
              value={<span style={{ fontSize: '12px', wordBreak: 'break-word' }}>{firstMessage.userAgent}</span>}
            />
          )}

          {firstMessage?.query && Object.keys(firstMessage.query).length > 0 && (
            <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
              <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>
                {'Query Parameters'}
              </div>
              <pre style={{ margin: 0, fontSize: '12px', color: '#000000', paddingLeft: '16px' }}>
                {JSON.stringify(firstMessage.query, null, 2)}
              </pre>
            </div>
          )}

          {firstMessage?.headers && (
            <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
              <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>{'Headers'}</div>
              {renderHeaders(firstMessage.headers)}
            </div>
          )}
        </>
      )}

      {/* Response-specific fields */}
      {isResponse && context && (
        <>
          {context.method && <Field title={'Method'} value={context.method} />}

          {context.statusCode !== undefined && (
            <Field
              title={'Status Code'}
              value={
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: getStatusColor(context.statusCode)
                  }}
                >
                  {context.statusCode}
                </span>
              }
            />
          )}

          {context.url &&
            (() => {
              const { baseUrl, queryString } = splitUrl(context.url);
              return (
                <>
                  <Field
                    title={'URL'}
                    value={
                      <span style={{ fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>
                        {baseUrl}
                      </span>
                    }
                  />
                  {queryString && (
                    <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                      <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>
                        {'Query String'}
                      </div>
                      <div style={{ paddingLeft: '16px', fontSize: '11px' }}>
                        <ReactJson
                          collapsed={1}
                          displayDataTypes={false}
                          displayObjectSize={false}
                          enableClipboard={true}
                          name={false}
                          src={qs.parse(queryString)}
                          style={{ fontSize: '11px' }}
                          theme={{
                            base00: '#ffffff',
                            base01: '#f5f5f5',
                            base02: '#e0e0e0',
                            base03: '#999999',
                            base04: '#666666',
                            base05: '#333333',
                            base06: '#1a1a1a',
                            base07: '#000000',
                            base08: '#d32f2f',
                            base09: '#f57c00',
                            base0A: '#fbc02d',
                            base0B: '#388e3c',
                            base0C: '#0097a7',
                            base0D: '#1976d2',
                            base0E: '#7b1fa2',
                            base0F: '#5d4037'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

          {firstMessage?.duration !== undefined && <Field title={'Duration'} value={`${firstMessage.duration}ms`} />}

          {firstMessage?.responseHeaders && (
            <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
              <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>{'Headers'}</div>
              {renderHeaders(firstMessage.responseHeaders)}
            </div>
          )}

          {firstMessage?.responseBody !== undefined && (
            <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
              <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>{'Body'}</div>
              <div style={{ paddingLeft: '16px', fontSize: '11px' }}>
                <ReactJson
                  collapsed={1}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard={true}
                  name={false}
                  src={firstMessage.responseBody}
                  style={{ fontSize: '11px' }}
                  theme={{
                    base00: '#ffffff',
                    base01: '#f5f5f5',
                    base02: '#e0e0e0',
                    base03: '#999999',
                    base04: '#666666',
                    base05: '#333333',
                    base06: '#1a1a1a',
                    base07: '#000000',
                    base08: '#d32f2f',
                    base09: '#f57c00',
                    base0A: '#fbc02d',
                    base0B: '#388e3c',
                    base0C: '#0097a7',
                    base0D: '#1976d2',
                    base0E: '#7b1fa2',
                    base0F: '#5d4037'
                  }}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Error-specific fields */}
      {isError && context && (
        <>
          {context.method && <Field title={'Method'} value={context.method} />}

          {context.statusCode !== undefined && (
            <Field
              title={'Status Code'}
              value={
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: getStatusColor(context.statusCode)
                  }}
                >
                  {context.statusCode}
                </span>
              }
            />
          )}

          {context.url &&
            (() => {
              const { baseUrl, queryString } = splitUrl(context.url);
              return (
                <>
                  <Field
                    title={'URL'}
                    value={
                      <span style={{ fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>
                        {baseUrl}
                      </span>
                    }
                  />
                  {queryString && (
                    <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                      <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>
                        {'Query String'}
                      </div>
                      <div style={{ paddingLeft: '16px', fontSize: '11px' }}>
                        <ReactJson
                          collapsed={1}
                          displayDataTypes={false}
                          displayObjectSize={false}
                          enableClipboard={true}
                          name={false}
                          src={qs.parse(queryString)}
                          style={{ fontSize: '11px' }}
                          theme={{
                            base00: '#ffffff',
                            base01: '#f5f5f5',
                            base02: '#e0e0e0',
                            base03: '#999999',
                            base04: '#666666',
                            base05: '#333333',
                            base06: '#1a1a1a',
                            base07: '#000000',
                            base08: '#d32f2f',
                            base09: '#f57c00',
                            base0A: '#fbc02d',
                            base0B: '#388e3c',
                            base0C: '#0097a7',
                            base0D: '#1976d2',
                            base0E: '#7b1fa2',
                            base0F: '#5d4037'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

          {messages && messages.length > 0 && (
            <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
              <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>{'Error Messages'}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {messages.map((message: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      padding: '8px 12px',
                      background: '#fff1f0',
                      borderRadius: '4px',
                      borderLeft: '3px solid #f5222d'
                    }}
                  >
                    {typeof message === 'string' ? (
                      <span style={{ fontSize: '13px', color: '#000000', lineHeight: '1.6', wordBreak: 'break-word' }}>
                        {message}
                      </span>
                    ) : (
                      <div>
                        {message.errorType && (
                          <div
                            style={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#f5222d',
                              marginBottom: '4px',
                              wordBreak: 'break-word'
                            }}
                          >
                            {message.errorType}
                          </div>
                        )}
                        {message.message && (
                          <div
                            style={{ fontSize: '13px', color: '#000000', marginBottom: '8px', wordBreak: 'break-word' }}
                          >
                            {message.message}
                          </div>
                        )}
                        <pre
                          style={{
                            margin: 0,
                            fontSize: '11px',
                            color: '#666',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word'
                          }}
                        >
                          {JSON.stringify(message, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Task-specific fields */}
      {isTask && context && (
        <>
          {context.method && (
            <Field
              title={'Method'}
              value={
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: methodColors[context.method] || '#d9d9d9'
                  }}
                >
                  {context.method}
                </span>
              }
            />
          )}

          {context.statusCode !== undefined && (
            <Field
              title={'Status Code'}
              value={
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: context.statusCode >= 400 ? '#f5222d' : '#1890ff'
                  }}
                >
                  {context.statusCode}
                </span>
              }
            />
          )}

          {context.url &&
            (() => {
              const { baseUrl, queryString } = splitUrl(context.url);
              return (
                <>
                  <Field
                    title={'URL'}
                    value={
                      <span style={{ fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>
                        {baseUrl}
                      </span>
                    }
                  />
                  {queryString && (
                    <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                      <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>
                        {'Query String'}
                      </div>
                      <div style={{ paddingLeft: '16px', fontSize: '11px' }}>
                        <ReactJson
                          collapsed={1}
                          displayDataTypes={false}
                          displayObjectSize={false}
                          enableClipboard={true}
                          name={false}
                          src={qs.parse(queryString)}
                          style={{ fontSize: '11px' }}
                          theme={{
                            base00: '#ffffff',
                            base01: '#f5f5f5',
                            base02: '#e0e0e0',
                            base03: '#999999',
                            base04: '#666666',
                            base05: '#333333',
                            base06: '#1a1a1a',
                            base07: '#000000',
                            base08: '#d32f2f',
                            base09: '#f57c00',
                            base0A: '#fbc02d',
                            base0B: '#388e3c',
                            base0C: '#0097a7',
                            base0D: '#1976d2',
                            base0E: '#7b1fa2',
                            base0F: '#5d4037'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

          {messages && messages.length > 0 && (
            <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
              <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>{'Messages'}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {messages.map((message: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      padding: '8px 12px',
                      background: 'rgba(24, 144, 255, 0.05)',
                      borderRadius: '4px',
                      borderLeft: '3px solid #1890ff'
                    }}
                  >
                    {typeof message === 'string' ? (
                      <span style={{ fontSize: '13px', color: '#000000', lineHeight: '1.6', wordBreak: 'break-word' }}>
                        {message}
                      </span>
                    ) : (
                      <div style={{ fontSize: '11px' }}>
                        <ReactJson
                          collapsed={1}
                          displayDataTypes={false}
                          displayObjectSize={false}
                          enableClipboard={true}
                          name={false}
                          src={message}
                          style={{ fontSize: '11px' }}
                          theme={{
                            base00: '#ffffff',
                            base01: '#f5f5f5',
                            base02: '#e0e0e0',
                            base03: '#999999',
                            base04: '#666666',
                            base05: '#333333',
                            base06: '#1a1a1a',
                            base07: '#000000',
                            base08: '#d32f2f',
                            base09: '#f57c00',
                            base0A: '#fbc02d',
                            base0B: '#388e3c',
                            base0C: '#0097a7',
                            base0D: '#1976d2',
                            base0E: '#7b1fa2',
                            base0F: '#5d4037'
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Task without context - show as JSON */}
      {isTask && !context && (
        <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
          <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>{'Body'}</div>
          <div style={{ paddingLeft: '16px', fontSize: '11px' }}>
            <ReactJson
              collapsed={1}
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={true}
              name={false}
              src={parsedBody || item.body}
              style={{ fontSize: '11px' }}
              theme={{
                base00: '#ffffff',
                base01: '#f5f5f5',
                base02: '#e0e0e0',
                base03: '#999999',
                base04: '#666666',
                base05: '#333333',
                base06: '#1a1a1a',
                base07: '#000000',
                base08: '#d32f2f',
                base09: '#f57c00',
                base0A: '#fbc02d',
                base0B: '#388e3c',
                base0C: '#0097a7',
                base0D: '#1976d2',
                base0E: '#7b1fa2',
                base0F: '#5d4037'
              }}
            />
          </div>
        </div>
      )}

      {/* Info and Verbose specific fields */}
      {(isInfo || isVerbose) && (
        <div style={{ paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
          <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>{'Body'}</div>
          <div style={{ paddingLeft: '16px', fontSize: '11px' }}>
            <ReactJson
              collapsed={1}
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={true}
              name={false}
              src={parsedBody || item.body}
              style={{ fontSize: '11px' }}
              theme={{
                base00: '#ffffff',
                base01: '#f5f5f5',
                base02: '#e0e0e0',
                base03: '#999999',
                base04: '#666666',
                base05: '#333333',
                base06: '#1a1a1a',
                base07: '#000000',
                base08: '#d32f2f',
                base09: '#f57c00',
                base0A: '#fbc02d',
                base0B: '#388e3c',
                base0C: '#0097a7',
                base0D: '#1976d2',
                base0E: '#7b1fa2',
                base0F: '#5d4037'
              }}
            />
          </div>
        </div>
      )}

      {!isRequest && !isResponse && !isError && !isInfo && !isVerbose && !isTask && (
        <Field title={'Body'} value={<LogBodyRendererFlat body={item.body} type={item.type} />} />
      )}

      <div style={{ paddingTop: '16px', borderTop: '1px solid #d9d9d9', marginTop: '16px' }}>
        <div style={{ color: 'rgb(29, 147, 51)', fontWeight: 500, marginBottom: '12px' }}>{'Raw'}</div>
        <div style={{ fontSize: '11px' }}>
          <ReactJson
            collapsed={1}
            displayDataTypes={false}
            displayObjectSize={false}
            enableClipboard={true}
            name={false}
            src={rawData}
            style={{ fontSize: '11px' }}
            theme={{
              base00: '#ffffff',
              base01: '#f5f5f5',
              base02: '#e0e0e0',
              base03: '#999999',
              base04: '#666666',
              base05: '#333333',
              base06: '#1a1a1a',
              base07: '#000000',
              base08: '#d32f2f',
              base09: '#f57c00',
              base0A: '#fbc02d',
              base0B: '#388e3c',
              base0C: '#0097a7',
              base0D: '#1976d2',
              base0E: '#7b1fa2',
              base0F: '#5d4037'
            }}
          />
        </div>
      </div>
    </div>
  );
}
